/** @jsx jsx */
/** @jsxFrag React.Fragment */
import React, { useRef, useState, useCallback, useContext, useEffect } from 'react';
import type { FC } from 'react';
import { styled, cssMap, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import type { StylesConfig } from '@atlaskit/select/types';

import { LazyPageLabelsAutomationDiscovery } from '@confluence/automation-discovery/entry-points/PageLabelsAutomationDiscovery.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import {
	VIEW_PAGE_LABELS_EXPERIENCE,
	AUTOMATION_DISCOVERY_ADD_LABEL_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStart,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { ContentAppearanceType } from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import {
	LABELS_SHORTCUT,
	UNIVERSAL_LABELS_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { fg } from '@confluence/feature-gating';

import type { CustomOptionType } from './LabelsSelect.tsx';
import { LabelsSelect } from './LabelsSelect.tsx';
import { CustomControl } from './LabelsSelectComponent.tsx';

type ViewPageLabelsContainerProps = {
	isDefaultWidth: boolean;
};
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ViewPageLabelsContainer = styled.div<ViewPageLabelsContainerProps>({
	margin: `0 auto ${token('space.500')} auto`,
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	maxWidth: (props) => (props.isDefaultWidth ? '760px' : 'unset'),
});

const controlStyles = cssMap({
	root: {
		margin: `0 -10px ${token('space.200')}`,
		backgroundColor: token('elevation.surface'),
		'&:hover, &:focus, &:active, &:focus-within': {
			backgroundColor: token('elevation.surface'),
			borderColor: token('color.border'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:not(&:focus-within, &:hover)': {
			borderColor: 'transparent',
		},
	},
});
const viewPageStyles: StylesConfig<CustomOptionType, true> = {
	container: (provided) => ({
		...provided,
		margin: `0 -10px ${token('space.200')}`,
	}),
	control: (provided) => ({
		...provided,
		backgroundColor: token('elevation.surface'),
		'&:hover, &:focus, &:active, &:focus-within': {
			backgroundColor: token('elevation.surface'),
			borderColor: token('color.border'),
		},
		':not(&:focus-within, :hover)': {
			borderColor: 'transparent',
		},
	}),
	menu: (provided) => ({
		...provided,
		zIndex: 5,
	}),
};

type ViewPageLabelsComponentProps = {
	pageWidthType: string;
	contentId: string;
	spaceKey: string | undefined;
	spaceId: string | undefined | null;
	contentSubType?: string | null;
};

export const ViewPageLabelsComponent: FC<ViewPageLabelsComponentProps> = ({
	pageWidthType,
	contentId,
	spaceKey,
}) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const [shouldDisplayAutomationDiscovery, setShouldDisplayAutomationDiscovery] = useState(false);
	const labelsRef = useRef<HTMLDivElement>(null);

	const experienceTracker = useContext(ExperienceTrackerContext);
	const { edition } = useSessionData();

	const memoizedComponents = React.useMemo(
		() => ({
			Control: (props) => {
				return <CustomControl {...props} xcss={controlStyles.root} />;
			},
		}),
		[],
	);

	useEffect(() => {
		// unmount LazyPageLabelsAutomationDiscovery if the user navigates to another page
		if (shouldDisplayAutomationDiscovery) {
			setShouldDisplayAutomationDiscovery(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId]);

	const checkShouldDisplayAutomationDiscovery = useCallback(() => {
		// Automation is a premium feature
		if (edition === ConfluenceEdition.PREMIUM || fg('a4c_free_and_standard_editions')) {
			const hasDismissedDiscoveryMessage = localStorage.getItemAsBoolean(
				keys.AUTOMATION_DISCOVER_ADD_LABEL_TO_PAGE,
			);

			// Don't render the experience if they've already seen and dismissed it
			if (!hasDismissedDiscoveryMessage) {
				setShouldDisplayAutomationDiscovery(true);
				experienceTracker.start({
					name: AUTOMATION_DISCOVERY_ADD_LABEL_EXPERIENCE,
				});
			}
		}
	}, [edition, experienceTracker]);

	const hideDiscoveryMessage = () => {
		setShouldDisplayAutomationDiscovery(false);
	};

	const openLabels = (e) => {
		// prevents text from being entered when pressing shortcut key
		e.preventDefault();
		labelsRef.current?.focus();
	};

	return (
		<>
			<ExperienceStart id={contentId} name={VIEW_PAGE_LABELS_EXPERIENCE} />
			<ViewPageLabelsContainer
				isDefaultWidth={JSON.parse(pageWidthType) === ContentAppearanceType.DEFAULT}
				data-testid="view-page-labels-container"
				data-vc="view-page-labels-container"
				{...ssrPlaceholderIdProp}
			>
				<GeneralShortcutListener
					deprecatedAccelerator={LABELS_SHORTCUT}
					accelerator={UNIVERSAL_LABELS_SHORTCUT}
					listener={openLabels}
				/>
				<LabelsSelect
					key={`labels-select-${contentId}`}
					source="viewPage"
					experienceName={VIEW_PAGE_LABELS_EXPERIENCE}
					contentId={contentId}
					spaceKey={spaceKey}
					styles={viewPageStyles}
					components={memoizedComponents}
					ref={labelsRef}
					maxMenuHeight={208}
					//@ts-ignore react-select types
					onAdded={checkShouldDisplayAutomationDiscovery}
				/>
				{shouldDisplayAutomationDiscovery && (
					<LazyPageLabelsAutomationDiscovery
						spaceKey={spaceKey}
						onDismiss={hideDiscoveryMessage}
						onProceed={hideDiscoveryMessage}
					/>
				)}
			</ViewPageLabelsContainer>
		</>
	);
};

export const ViewPageLabels: React.FC<ViewPageLabelsComponentProps> = withErrorBoundary({
	attribution: Attribution.DISCO,
})(ViewPageLabelsComponent);
